"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ruleForCarrier,
  validateWritingNumber,
  sanitizeWritingNumberInput,
  normalizeWritingNumber,
} from "../../writingNumberRules";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { useDirect130Host } from "@/app/_module/utils/direct130HostContext";

import {
  techficientService,
  type TechficientProduct,
} from "@/app/_module/services/techficientService";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

type Step =
  | "profile"
  | "licenses"
  | "carriers"
  | "review"
  | "processing"
  | "success";

/**
 * Phases shown on the processing overlay between Review and Success. Visual
 * only — the actual `/onboard` API call runs in parallel; phases 0-2 advance
 * on a timer, phase 3 spins until the API resolves. Total minimum ~2.1s so
 * a fast API doesn't feel like a flash.
 */
const PROCESSING_PHASES = [
  { label: "Verifying your NPN" },
  { label: "Registering state licenses" },
  { label: "Linking carrier appointments" },
  { label: "Activating producer profile" },
] as const;
const PHASE_DURATION_MS = 700;

const ALL_STEPS: Array<{ id: Step; label: string }> = [
  { id: "profile", label: "Profile" },
  { id: "licenses", label: "Licenses" },
  { id: "carriers", label: "Carriers" },
  { id: "review", label: "Review" },
];

interface OnboardingProps {
  defaultAgentName?: string;
  defaultEmail?: string;
  /** NPN from the user record — collected + validated at signup. */
  defaultNpn?: string;
  /** State where the NPN is registered — used to pre-fill the first state-license row. */
  defaultNpnState?: string;
  /**
   * Pre-existing state licenses — when present (i.e. agent is editing
   * after onboarding), the Licenses step seeds with these instead of an
   * empty row. The Activate button text changes to "Save changes".
   */
  defaultLicenses?: Array<{ stateCode: string; licenseNumber: string }>;
  /** Pre-existing carrier contracts — same logic. */
  defaultContracts?: Array<{ carrierCode: string; writingNumber: string }>;
  /** Optional dismiss handle — shown as a Cancel button in edit mode. */
  onCancel?: () => void;
  onActivated?: () => void;
}

/**
 * Quote & Enroll first-run setup. Wires the agent's NPN + state licenses +
 * carrier writing numbers, then provisions their producer profile against
 * Techficient (`/user/create`). Same visual vocabulary as the rest of the
 * CRM — small icon-chip headers, clean white surfaces, no gradient theatre.
 */
export function QuoteEnrollOnboarding({
  defaultAgentName,
  defaultEmail,
  defaultNpn,
  defaultNpnState,
  defaultLicenses,
  defaultContracts,
  onCancel,
  onActivated,
}: OnboardingProps) {
  const onDirect130 = useDirect130Host(); // Direct130's agents quote across Direct130's appointments, not MRFG's
  const queryClient = useQueryClient();
  // Edit mode = agent already provisioned, opening the wizard from a
  // "Manage profile" affordance. Skip Profile (NPN is locked from signup
  // either way) AND pre-seed licenses + carrier contracts from what's
  // already on the techficient_user_link row.
  const isEditMode = !!(defaultLicenses?.length || defaultContracts?.length);
  // Signup already collects + state-validates the NPN, so when it's on file
  // we skip the Profile step entirely and drop the agent straight into
  // Licenses. They confirm it on the Review step anyway. Agents who don't
  // have an NPN on record (legacy users from before signup required it)
  // still see Profile first so they can supply one.
  const hasNpnOnFile = !!(defaultNpn && defaultNpn.trim());
  const STEPS = useMemo(
    () => (hasNpnOnFile ? ALL_STEPS.filter((s) => s.id !== "profile") : ALL_STEPS),
    [hasNpnOnFile],
  );
  const [step, setStep] = useState<Step>(hasNpnOnFile ? "licenses" : "profile");

  const [npn, setNpn] = useState(defaultNpn || "");
  const [licenses, setLicenses] = useState<
    Array<{ stateCode: string; licenseNumber: string }>
  >(() => {
    if (defaultLicenses?.length) {
      return defaultLicenses.map((l) => ({
        stateCode: (l.stateCode || "").toUpperCase(),
        licenseNumber: l.licenseNumber || "",
      }));
    }
    return [
      {
        stateCode: defaultNpnState?.toUpperCase() || "",
        // Most states issue the producer license under the NPN itself, so
        // the NPN is the right default — editable for states that issue
        // their own number (e.g. FL/CA legacy formats).
        licenseNumber: defaultNpn || "",
      },
    ];
  });
  const [contracts, setContracts] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const c of defaultContracts || []) {
      if (c.carrierCode) seed[c.carrierCode] = c.writingNumber || "";
    }
    return seed;
  });
  const [carrierBulkOpen, setCarrierBulkOpen] = useState(false);
  const [carrierBulkText, setCarrierBulkText] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Processing overlay state.
  //  - activePhase: which phase row is currently spinning (0..n-1)
  //  - apiResolved: did the /onboard API resolve successfully? phase 3
  //    keeps spinning until this flips so we always end on a satisfying
  //    checkmark, never an awkward freeze.
  const [activePhase, setActivePhase] = useState(0);
  const [apiResolved, setApiResolved] = useState(false);
  const phaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Advance phases 0 → 1 → 2 → 3 on a steady cadence while we're on the
  // processing step. Phase 3 (the last) just sits and spins until the API
  // resolves — `apiResolved` then triggers the success transition.
  useEffect(() => {
    if (step !== "processing") return;
    if (activePhase < PROCESSING_PHASES.length - 1) {
      phaseTimerRef.current = setTimeout(
        () => setActivePhase((p) => p + 1),
        PHASE_DURATION_MS,
      );
      return () => {
        if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
      };
    }
    // Phase 3 active — wait for the API.
    if (apiResolved) {
      // Hold the final check for a beat so the eye lands on it, then move
      // to the success step. Gives the whole thing a deliberate "land".
      successTimerRef.current = setTimeout(() => setStep("success"), 450);
      return () => {
        if (successTimerRef.current) clearTimeout(successTimerRef.current);
      };
    }
  }, [step, activePhase, apiResolved]);

  // Auto-dismiss the success screen after a beat so the agent lands on the
  // normal Quote & Enroll surface without having to click anything.
  useEffect(() => {
    if (step !== "success") return;
    const t = setTimeout(() => onActivated?.(), 1800);
    return () => clearTimeout(t);
  }, [step, onActivated]);

  const [forceRefresh, setForceRefresh] = useState(false);
  const {
    data: products = [],
    isLoading: productsLoading,
    isFetching: productsRefreshing,
    refetch: refetchProducts,
  } = useQuery({
    queryKey: ["techficient", "products", "all", forceRefresh],
    queryFn: () => techficientService.listProducts(false, forceRefresh),
    staleTime: 5 * 60_000,
  });
  const carriers = useMemo(() => {
    const m = new Map<string, { carrierCode: string; carrierName: string }>();
    for (const p of products as TechficientProduct[]) {
      if (!m.has(p.carrierCode)) {
        m.set(p.carrierCode, {
          carrierCode: p.carrierCode,
          carrierName: p.carrierName,
        });
      }
    }
    return Array.from(m.values()).sort((a, b) =>
      a.carrierName.localeCompare(b.carrierName),
    );
  }, [products]);

  const idx = STEPS.findIndex((s) => s.id === step);
  // Block Continue only when a filled writing number violates its carrier's
  // API-verified format rule (F&G 9–10, UHL exactly 10, American Amicable
  // exactly 10 digits; everyone else loose). Empty rows are fine (optional).
  const carrierRowsValid = Object.entries(contracts).every(([code, v]) =>
    !v || !v.trim() ? true : validateWritingNumber(code, v) === null,
  );

  const canContinue: Record<Step, boolean> = {
    profile: !!npn.trim(),
    licenses:
      licenses.filter((l) => l.stateCode && l.licenseNumber.trim()).length > 0,
    carriers: carrierRowsValid,
    review: carrierRowsValid,
    processing: true,
    success: true,
  };

  const next = () => {
    if (step === "review") return submit();
    const i = STEPS.findIndex((s) => s.id === step);
    if (i >= 0 && i < STEPS.length - 1) setStep(STEPS[i + 1].id);
  };
  const back = () => {
    const i = STEPS.findIndex((s) => s.id === step);
    if (i > 0) setStep(STEPS[i - 1].id);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    setActivePhase(0);
    setApiResolved(false);
    // Switch to the processing step immediately — the phase timer + API
    // call run in parallel from here. The API result feeds `apiResolved`,
    // which the phase-advance effect listens for to trigger success.
    setStep("processing");
    try {
      const cleanLicenses = licenses
        .filter((l) => l.stateCode && l.licenseNumber.trim())
        .map((l) => ({
          stateCode: l.stateCode.toUpperCase(),
          licenseNumber: l.licenseNumber.trim(),
        }));
      const carrierContracts = Object.entries(contracts)
        .filter(([, n]) => n && n.trim())
        .map(([carrierCode, writingNumber]) => ({
          carrierCode,
          // Normalize to the carrier's exact API format (e.g. AmAm 7-digit
          // → zero-padded to 10) so the agent types their real number and
          // we submit what Techficient expects.
          writingNumber: normalizeWritingNumber(carrierCode, writingNumber),
        }));
      await techficientService.onboard({
        npn: npn.trim(),
        stateLicenses: cleanLicenses,
        carrierContracts,
      });
      queryClient.invalidateQueries({ queryKey: ["techficient", "eligibility"] });
      setApiResolved(true);
    } catch (e: any) {
      // Bail back to Review with a clear error so the agent can adjust
      // and try again. The processing overlay disappears immediately.
      const raw =
        e?.response?.data?.message ||
        e?.message ||
        "Couldn't activate — check your NPN and writing numbers and try again.";
      // White-label: scrub vendor mentions from surfaced error text.
      setError(String(raw).replace(/techficient|techf\.com/gi, "carrier"));
      setStep("review");
    } finally {
      setSubmitting(false);
    }
  };

  const applyBulkContracts = () => {
    const next: Record<string, string> = { ...contracts };
    const valid = new Set(carriers.map((c) => c.carrierCode));
    let applied = 0;
    let skipped = 0;
    for (const raw of carrierBulkText.split(/\r?\n/)) {
      const line = raw.trim();
      if (!line) continue;
      const parts = line.split(/[,\t]/).map((p) => p.trim());
      if (parts.length < 2) {
        skipped++;
        continue;
      }
      const [code, num] = parts;
      if (!valid.has(code)) {
        skipped++;
        continue;
      }
      // Strip any non-digit from pasted writing numbers (matches the inline
      // input behavior) — agents pasting BGA exports sometimes have extra
      // dashes or spaces.
      const clean = (num || "").replace(/\D+/g, "");
      if (!clean) {
        skipped++;
        continue;
      }
      next[code] = clean;
      applied++;
    }
    setContracts(next);
    setCarrierBulkOpen(false);
    setCarrierBulkText("");
    if (skipped > 0) {
      setError(`Applied ${applied}. Skipped ${skipped} (unknown carrier code).`);
      setTimeout(() => setError(null), 4000);
    }
  };

  // ─────────────────────────── render ───────────────────────────

  return (
    <div className="space-y-6 text-center">
      {/* Header — centered, matches the rest of /quote-enroll. Animated
          "wiring" SVG replaces the static icon chip to tie to the copy
          ("InsuraBot AI will wire your producer profile…") — a central node
          (your profile) with three carrier nodes connected by lines that
          pulse with traveling-dash energy. Pure SVG + CSS, no JS, no deps. */}
      <header className="flex flex-col items-center gap-4">
        <WiringIllustration />
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEditMode ? "Manage producer profile" : "Set up Quote & Enroll"}
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-md mx-auto">
            {isEditMode
              ? "Add or update your state licenses and carrier writing numbers. Changes take effect instantly."
              : `One-time setup — about 3 minutes. InsuraBot AI will wire your producer profile so you can quote and apply across ${onDirect130 ? "Direct130" : "MRFG"}'s appointed carriers.`}
          </p>
        </div>
      </header>

      {/* Body — each step gets a key so React re-mounts on transition,
          letting the per-step animate-in classes fire fresh. */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 md:p-6 text-left overflow-hidden transition-all duration-500">
        {step !== "success" && step !== "processing" && (
          <div className="mb-5 pb-4 border-b border-gray-100 animate-in fade-in duration-300">
            <StepIndicator steps={STEPS} currentIdx={idx} />
          </div>
        )}
        {/* Per-step wrapper keyed by step so React re-mounts the child
            on transition, triggering the fade/slide animations cleanly. */}
        <div
          key={step}
          className="animate-in fade-in slide-in-from-bottom-1 duration-400"
        >
        {step === "profile" && (
          <StepProfile
            npn={npn}
            setNpn={setNpn}
            name={defaultAgentName}
            email={defaultEmail}
          />
        )}
        {step === "licenses" && (
          <StepLicenses licenses={licenses} setLicenses={setLicenses} npn={npn} />
        )}
        {step === "carriers" && (
          <StepCarriers
            carriers={carriers}
            contracts={contracts}
            setContracts={setContracts}
            productsLoading={productsLoading}
            productsRefreshing={productsRefreshing && forceRefresh}
            onRefresh={async () => {
              // Toggle the queryKey so the cache entry rebuilds with the
              // refresh=true flag → backend busts its 1-hr cache → fresh
              // list comes back. Reset to false afterwards so subsequent
              // navigation doesn't keep slamming Techficient.
              setForceRefresh(true);
              await refetchProducts();
              setTimeout(() => setForceRefresh(false), 500);
            }}
            onOpenBulk={() => setCarrierBulkOpen(true)}
          />
        )}
        {step === "review" && (
          <StepReview
            name={defaultAgentName}
            npn={npn}
            licenses={licenses.filter(
              (l) => l.stateCode && l.licenseNumber.trim(),
            )}
            contracts={contracts}
            carriers={carriers}
          />
        )}
        {step === "processing" && <StepProcessing activePhase={activePhase} />}
        {step === "success" && <StepSuccess isEditMode={isEditMode} />}
        </div>

        {error && step !== "success" && (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
            <X className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Footer nav */}
      {step !== "success" && step !== "processing" && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              onClick={back}
              disabled={submitting || idx === 0}
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back
            </Button>
            {isEditMode && onCancel && idx === 0 && (
              <Button
                type="button"
                variant="ghost"
                onClick={onCancel}
                disabled={submitting}
              >
                Cancel
              </Button>
            )}
          </div>
          <Button
            type="button"
            onClick={next}
            disabled={!canContinue[step] || submitting}
            className="min-w-[140px]"
          >
            {submitting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                {isEditMode ? "Saving…" : "Activating…"}
              </>
            ) : step === "review" ? (
              isEditMode ? "Save changes" : "Activate"
            ) : (
              <>
                Continue
                <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </>
            )}
          </Button>
        </div>
      )}

      {carrierBulkOpen && (
        <BulkPasteModal
          value={carrierBulkText}
          setValue={setCarrierBulkText}
          carriers={carriers}
          onApply={applyBulkContracts}
          onClose={() => setCarrierBulkOpen(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────── wiring illustration ───────────────────────────

/**
 * "Producer credential" illustration — laptop with a form on screen
 * (checkmarks animating in, scanline sweeping across) and a producer
 * license paper sliding out behind it with a notarized stamp pulsing.
 * Tells the story of setup happening: form gets filled → certified
 * credential comes out.
 *
 * Semisolid style — flat fills + strokes in the brand primary, no
 * gradients, no AI-template glow. Pure inline SVG + a single <style>
 * for keyframes — no framer-motion, no deps, never bounces on first
 * paint.
 */
function WiringIllustration() {
  return (
    // text-primary binds the whole SVG to the white-label brand color via
    // currentColor inheritance. MRFG, Direct130, or any future white-label
    // sees its own brand color — never our default blue.
    <div className="relative text-primary">
      <style>{`
        /* Paper float — tiny vertical breathing on the credential page so
           it doesn't feel pasted on. */
        @keyframes qe-paper-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-1.2px); }
        }
        /* Stamp pulse — the seal on the credential paper pulses like a
           freshly-applied notarization. */
        @keyframes qe-stamp-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.12); }
        }
        @keyframes qe-stamp-halo {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }

        .qe-paper       { transform-origin: 88px 50px; animation: qe-paper-float 4s ease-in-out infinite; }
        .qe-stamp       { transform-origin: center; animation: qe-stamp-pulse 2.4s ease-in-out infinite; }
        .qe-stamp-halo  { animation: qe-stamp-halo 2.4s ease-in-out infinite; }
      `}</style>

      <svg
        viewBox="0 0 120 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-16 w-24 overflow-visible"
        aria-hidden="true"
      >
        {/* ─── Laptop ─── (drawn FIRST so the credential paper layers over it) */}
        <g>
          {/* Screen body — FULLY opaque so the credential paper behind it
              doesn't bleed through and read as stray marks at the top edge. */}
          <rect
            x="6"
            y="10"
            width="58"
            height="42"
            rx="3"
            fill="currentColor"
          />
          {/* Inner screen — pure white, fully opaque. */}
          <rect
            x="9"
            y="13"
            width="52"
            height="36"
            rx="1.5"
            fill="white"
          />

          {/* Inside the screen — three static form rows. Earlier versions
              had blinking checkmarks + a moving scanline; at this render
              size they read as "dots flickering" rather than meaningful
              motion, so we strip them. The paper + stamp on top carry the
              storytelling motion. */}
          <g>
            <line x1="13" y1="22" x2="48" y2="22" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.4" strokeLinecap="round" />
            <line x1="13" y1="30" x2="52" y2="30" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.4" strokeLinecap="round" />
            <line x1="13" y1="38" x2="42" y2="38" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.4" strokeLinecap="round" />
          </g>

          {/* Laptop base — trapezoid keyboard deck */}
          <path
            d="M2 54 L68 54 L72 60 L-2 60 Z"
            fill="currentColor"
          />
          {/* Front lip / touchpad notch */}
          <rect x="28" y="56" width="14" height="1.6" rx="0.8" fill="white" fillOpacity="0.5" />
        </g>

        {/* ─── Credential paper — layered ON TOP of the laptop, tilted up
            and to the right like it's being lifted out. Subtle shadow under
            it (small offset rect) gives depth so it doesn't look pasted. ─── */}
        <g className="qe-paper">
          {/* Drop shadow band */}
          <rect x="64" y="11" width="44" height="48" rx="3" fill="black" fillOpacity="0.06" transform="rotate(6 64 11)" />
          <g transform="translate(62 6) rotate(6)">
            <rect
              x="0"
              y="0"
              width="46"
              height="52"
              rx="3"
              fill="white"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeOpacity="0.9"
            />
            {/* Header strip */}
            <rect x="4" y="4" width="38" height="6" rx="1.5" fill="currentColor" fillOpacity="0.22" />
            {/* Body text lines */}
            <line x1="4" y1="18" x2="32" y2="18" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="4" y1="24" x2="38" y2="24" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="4" y1="30" x2="28" y2="30" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.5" strokeLinecap="round" />
            {/* Signature line */}
            <line x1="4" y1="44" x2="22" y2="44" stroke="currentColor" strokeOpacity="0.6" strokeWidth="1.2" strokeLinecap="round" />

            {/* Notarized stamp — circle with a checkmark + soft halo */}
            <g transform="translate(35 40)">
              <circle r="8" className="qe-stamp-halo" fill="currentColor" fillOpacity="0.18" />
              <g className="qe-stamp">
                <circle r="6" fill="none" stroke="currentColor" strokeWidth="1.4" />
                <path
                  d="M-2.5 0 L-0.5 2 L3 -2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </g>
            </g>
          </g>
        </g>
      </svg>
    </div>
  );
}

// ─────────────────────────── step indicator ───────────────────────────

function StepIndicator({
  steps,
  currentIdx,
}: {
  steps: Array<{ id: Step; label: string }>;
  currentIdx: number;
}) {
  return (
    <ol className="flex items-center justify-center gap-3">
      {steps.map((s, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <li key={s.id} className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span
                className={`flex-shrink-0 h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-semibold border transition-colors ${
                  done
                    ? "bg-primary text-white border-primary"
                    : active
                    ? "bg-white text-primary border-primary"
                    : "bg-white text-gray-400 border-gray-200"
                }`}
              >
                {done ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              <span
                className={`text-xs font-medium ${
                  active ? "text-gray-900" : done ? "text-gray-700" : "text-gray-400"
                }`}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <span
                className={`w-8 h-px ${
                  done ? "bg-primary/50" : "bg-gray-200"
                }`}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ─────────────────────────── step content ───────────────────────────

function StepProfile({
  npn,
  setNpn,
  name,
  email,
}: {
  npn: string;
  setNpn: (v: string) => void;
  name?: string;
  email?: string;
}) {
  return (
    <div className="space-y-4">
      <SectionLabel>Producer profile</SectionLabel>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Name">
          <ReadOnly value={name || "—"} />
        </Field>
        <Field label="Email">
          <ReadOnly value={email || "—"} />
        </Field>
      </div>
      <Field label="NPN" required>
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="Your NPN"
          value={npn}
          onChange={(e) => setNpn(e.target.value.replace(/\D+/g, ""))}
          className="h-9 font-mono"
          maxLength={15}
          autoFocus
        />
        <p className="text-[11px] text-gray-500 mt-1">
          National Producer Number — look it up at{" "}
          <a
            href="https://nipr.com/help/look-up-your-npn"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2"
          >
            nipr.com
          </a>
          .
        </p>
      </Field>
    </div>
  );
}

function StepLicenses({
  licenses,
  setLicenses,
  npn,
}: {
  licenses: Array<{ stateCode: string; licenseNumber: string }>;
  setLicenses: (
    v: Array<{ stateCode: string; licenseNumber: string }>,
  ) => void;
  /** Most states license under the NPN — used as the default number for new rows. */
  npn?: string;
}) {
  const update = (i: number, patch: Partial<typeof licenses[0]>) => {
    const next = [...licenses];
    next[i] = { ...next[i], ...patch };
    setLicenses(next);
  };
  const add = () =>
    setLicenses([...licenses, { stateCode: "", licenseNumber: npn || "" }]);
  const remove = (i: number) => {
    const filtered = licenses.filter((_, idx) => idx !== i);
    setLicenses(filtered.length ? filtered : [{ stateCode: "", licenseNumber: "" }]);
  };
  return (
    <div className="space-y-3">
      <div>
        <SectionLabel>State licenses</SectionLabel>
        <p className="text-xs text-gray-500 mt-0.5">
          Where are you licensed to sell? Add at least one.
        </p>
      </div>
      <div className="space-y-2">
        {licenses.map((l, i) => (
          <div key={i} className="flex items-center gap-2">
            <select
              value={l.stateCode}
              onChange={(e) => update(i, { stateCode: e.target.value })}
              className="h-9 w-24 rounded-md border border-gray-200 bg-white px-2 text-sm focus:border-primary focus:outline-none"
            >
              <option value="">State</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="License number"
              value={l.licenseNumber}
              onChange={(e) =>
                // Strip everything that isn't a digit on the way in so an
                // agent can't paste "vrujbnreujbnrb" and stall the carrier-
                // side validation later.
                update(i, { licenseNumber: e.target.value.replace(/\D+/g, "") })
              }
              className="flex-1 h-9 font-mono text-sm"
              maxLength={32}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="flex-shrink-0 h-9 w-9 rounded-md hover:bg-gray-50 text-gray-400 hover:text-gray-600 flex items-center justify-center"
              aria-label="Remove"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={add}>
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Add state
      </Button>
    </div>
  );
}

function StepCarriers({
  carriers,
  contracts,
  setContracts,
  productsLoading,
  productsRefreshing,
  onRefresh,
  onOpenBulk,
}: {
  carriers: Array<{ carrierCode: string; carrierName: string }>;
  contracts: Record<string, string>;
  setContracts: (v: Record<string, string>) => void;
  productsLoading: boolean;
  productsRefreshing: boolean;
  onRefresh: () => void;
  onOpenBulk: () => void;
}) {
  const filled = Object.values(contracts).filter((v) => v && v.trim()).length;
  return (
    <div className="space-y-3">
      <div>
        <SectionLabel>Carrier appointments</SectionLabel>
        <p className="text-xs text-gray-500 mt-0.5">
          Writing number per carrier. All optional — skip any you aren&apos;t
          appointed with.
        </p>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded">
            {filled}/{carriers.length}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={productsRefreshing}
            title="Pull the latest carrier list"
            className="h-8 w-8 p-0"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${productsRefreshing ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onOpenBulk}>
          Paste list
        </Button>
      </div>

      {productsLoading ? (
        <div className="py-8 flex items-center justify-center text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : (
        <div className="border border-gray-100 rounded-md divide-y divide-gray-100 max-h-[420px] overflow-y-auto">
          {carriers.map((c) => {
            const val = contracts[c.carrierCode] || "";
            const filled = !!val.trim();
            const rule = ruleForCarrier(c.carrierCode);
            // Per-carrier format (API-verified). Empty is fine; a filled-but-
            // invalid value flags in amber. Only the 3 carriers with real
            // rules (F&G / UHL / American Amicable) can flag here.
            const err = filled ? validateWritingNumber(c.carrierCode, val) : null;
            return (
              <div
                key={c.carrierCode}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50/50 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {c.carrierName}
                  </p>
                  {err ? (
                    <p className="text-[11px] text-amber-600 mt-0.5">{err}</p>
                  ) : rule.hint ? (
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {rule.hint}
                    </p>
                  ) : null}
                </div>
                <Input
                  type="text"
                  placeholder="Writing #"
                  value={val}
                  onChange={(e) =>
                    setContracts({
                      ...contracts,
                      [c.carrierCode]: sanitizeWritingNumberInput(
                        c.carrierCode,
                        e.target.value,
                      ),
                    })
                  }
                  className={`w-40 h-8 font-mono text-sm ${
                    err ? "border-amber-300 focus-visible:ring-amber-300" : ""
                  }`}
                  maxLength={rule.exact ?? rule.max ?? 20}
                />
                <span className="w-4 flex-shrink-0 flex items-center justify-center">
                  {filled && !err && (
                    <Check className="h-3.5 w-3.5 text-emerald-500" />
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StepReview({
  name,
  npn,
  licenses,
  contracts,
  carriers,
}: {
  name?: string;
  npn: string;
  licenses: Array<{ stateCode: string; licenseNumber: string }>;
  contracts: Record<string, string>;
  carriers: Array<{ carrierCode: string; carrierName: string }>;
}) {
  const carrierLookup = useMemo(
    () => new Map(carriers.map((c) => [c.carrierCode, c.carrierName])),
    [carriers],
  );
  const filled = Object.entries(contracts).filter(([, v]) => v && v.trim());
  return (
    <div className="space-y-4">
      <SectionLabel>Review</SectionLabel>
      <dl className="divide-y divide-gray-100 border border-gray-100 rounded-md">
        <ReviewRow label="Producer" value={name || "—"} />
        <ReviewRow label="NPN" value={npn} mono />
        <ReviewRow
          label={`State licenses (${licenses.length})`}
          value={
            licenses.length
              ? licenses
                  .map((l) => `${l.stateCode} · ${l.licenseNumber}`)
                  .join(", ")
              : "None"
          }
        />
        <ReviewRow
          label={`Carrier appointments (${filled.length})`}
          value={
            filled.length
              ? filled
                  .map(
                    ([code, num]) =>
                      `${carrierLookup.get(code) || code} · ${num}`,
                  )
                  .join(", ")
              : "None — add later"
          }
        />
      </dl>
      <div className="rounded-md bg-gray-50 border border-gray-100 p-3 text-xs text-gray-600 space-y-1.5">
        <p>
          <span className="font-medium text-gray-900">Activate</span> hands
          your producer profile to InsuraBot AI so you can quote and apply
          within seconds.
        </p>
        <p>
          You can add more states or update writing numbers any time from{" "}
          <span className="font-medium text-gray-900">
            Manage profile
          </span>{" "}
          on the Quote &amp; Enroll page.
        </p>
        <p>
          If a writing number is off, the carrier sends back a clear message
          and your other carriers keep working — nothing breaks.
        </p>
      </div>
    </div>
  );
}

/**
 * Processing overlay shown between Review and Success. Phases tick through
 * on a steady cadence (700ms each); the last phase ("Activating producer
 * profile") sits and spins until the API resolves. Each row fades in, then
 * spins, then locks to a green check — the visual progress of "InsuraBot
 * is wiring you up right now".
 *
 * Buttery: every transition uses the same 400ms ease-out curve so the eye
 * doesn't catch any of them changing direction.
 */
function StepProcessing({ activePhase }: { activePhase: number }) {
  return (
    <div className="py-4 text-center animate-in fade-in duration-500">
      <style>{`
        @keyframes qe-row-fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .qe-row-anim { animation: qe-row-fade-in 360ms ease-out both; }
      `}</style>
      {/* Enlarged hero illustration */}
      <div className="flex justify-center mb-1 scale-[1.4] origin-center">
        <WiringIllustration />
      </div>
      <h2 className="text-base font-semibold text-gray-900 mt-4">
        Activating your profile
      </h2>
      <p className="text-xs text-gray-500 mt-1">
        InsuraBot AI is wiring you up to your carriers.
      </p>

      <ol className="mt-5 space-y-2 max-w-sm mx-auto text-left">
        {PROCESSING_PHASES.map((phase, i) => {
          const done = i < activePhase;
          const active = i === activePhase;
          const pending = i > activePhase;
          return (
            <li
              key={phase.label}
              className="qe-row-anim flex items-center gap-3 text-sm rounded-md px-3 py-2 transition-colors duration-300"
              style={{
                animationDelay: `${i * 60}ms`,
                backgroundColor: active
                  ? "rgba(0,0,0,0.025)"
                  : "transparent",
              }}
            >
              <span
                className={`flex-shrink-0 h-5 w-5 rounded-full flex items-center justify-center transition-all duration-300 ${
                  done
                    ? "bg-emerald-500"
                    : active
                    ? "bg-primary/15"
                    : "bg-gray-100"
                }`}
              >
                {done ? (
                  <Check
                    className="h-3 w-3 text-white animate-in zoom-in duration-300"
                    strokeWidth={3}
                  />
                ) : active ? (
                  <Loader2 className="h-3 w-3 text-primary animate-spin" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                )}
              </span>
              <span
                className={`transition-colors duration-300 ${
                  done
                    ? "text-gray-700"
                    : active
                    ? "text-gray-900 font-medium"
                    : "text-gray-400"
                }`}
              >
                {phase.label}
                {active && "…"}
              </span>
              {/* Soft glow under the active row */}
              {active && !pending && (
                <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary/50 animate-pulse" />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * Success — big draw-on checkmark, brief headline, soft emerald glow. Wizard
 * auto-dismisses ~1.8s after this mounts (handled in the parent effect), so
 * no button is needed. Keeps motion light: SVG check strokes in over 600ms,
 * card content fades + slides in.
 */
function StepSuccess({ isEditMode }: { isEditMode: boolean }) {
  return (
    <div className="py-6 text-center animate-in fade-in slide-in-from-bottom-1 duration-500">
      <style>{`
        @keyframes qe-check-draw {
          to { stroke-dashoffset: 0; }
        }
        @keyframes qe-success-glow {
          0%   { transform: scale(0.85); opacity: 0; }
          60%  { transform: scale(1.05); opacity: 0.6; }
          100% { transform: scale(1.5);  opacity: 0; }
        }
        .qe-success-check {
          stroke-dasharray: 36;
          stroke-dashoffset: 36;
          animation: qe-check-draw 600ms ease-out 120ms forwards;
        }
        .qe-success-glow {
          animation: qe-success-glow 1.2s ease-out forwards;
        }
      `}</style>
      <div className="relative inline-flex items-center justify-center mb-4">
        {/* Emerald halo that expands + fades */}
        <span
          className="absolute inset-0 rounded-full bg-emerald-400/30"
          style={{ filter: "blur(8px)" }}
        />
        <span className="absolute inset-0 rounded-full bg-emerald-400/40 qe-success-glow" />
        {/* The check itself */}
        <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/30">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-7 w-7"
          >
            <path
              className="qe-success-check"
              d="M5 12.5 L10 17.5 L19 7.5"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
      <h2 className="text-xl font-semibold text-gray-900">
        {isEditMode ? "Changes saved" : "You're live"}
      </h2>
      <p className="text-sm text-gray-600 mt-1 max-w-md mx-auto">
        {isEditMode
          ? "Your producer profile is up to date and synced with the carriers."
          : "Quoting unlocked across your appointed carriers. Taking you in…"}
      </p>
    </div>
  );
}

// ─────────────────────────── small primitives ───────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
      {children}
    </p>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-600 font-medium mb-1">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function ReadOnly({ value }: { value: string }) {
  return (
    <p className="text-sm text-gray-700 bg-gray-50 rounded-md px-3 py-2 border border-gray-100 h-9 flex items-center">
      {value}
    </p>
  );
}

function ReviewRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 px-3 py-2.5 text-sm">
      <dt className="w-44 flex-shrink-0 text-xs text-gray-500 font-medium pt-0.5">
        {label}
      </dt>
      <dd
        className={`flex-1 text-gray-900 ${mono ? "font-mono" : ""}`}
        title={value}
      >
        {value}
      </dd>
    </div>
  );
}

function BulkPasteModal({
  value,
  setValue,
  carriers,
  onApply,
  onClose,
}: {
  value: string;
  setValue: (v: string) => void;
  carriers: Array<{ carrierCode: string; carrierName: string }>;
  onApply: () => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full p-5 border">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Paste writing numbers
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              One per line:{" "}
              <code className="bg-gray-100 px-1 rounded text-[11px]">
                carrier-code,writing-number
              </code>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={8}
          placeholder={"sbli,123456\nlga,9100000000\nuhl,55501234"}
          className="w-full rounded-md border border-gray-200 px-3 py-2 font-mono text-sm focus:border-primary focus:outline-none"
        />
        <details className="mt-2 text-xs text-gray-500">
          <summary className="cursor-pointer font-medium">
            Carrier codes ({carriers.length})
          </summary>
          <div className="mt-2 flex flex-wrap gap-1">
            {carriers.map((c) => (
              <code
                key={c.carrierCode}
                className="bg-gray-100 px-1.5 py-0.5 rounded text-[10px]"
                title={c.carrierName}
              >
                {c.carrierCode}
              </code>
            ))}
          </div>
        </details>
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button type="button" variant="ghost" onClick={onClose} size="sm">
            Cancel
          </Button>
          <Button type="button" onClick={onApply} size="sm">
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}

export default QuoteEnrollOnboarding;
